/**
 * `BookingRow` — the uniform table-style row for any itinerary item, plus its
 * small presentational helpers (`RowThumbnail`, `FlightLegThumbnail`,
 * `BookingPriceStatus`).
 */

import type { FlightLeg } from '@elara/client-core/lib/booking-metadata';
import type { ItineraryItem } from '@elara/client-core/models/ItineraryItemModel';
import type { RoomSelection } from '@elara/domain/models/schemas/room-schemas';

import React from 'react';

import { flightLegs, placeSubtitleChips, roomSubtitleChips } from '@elara/client-core/lib/booking-metadata';
import { cn } from '@elara/client-core/lib/utils';
import { bookingStatusFlagsOf } from '@elara/domain/booking-status';
import { isAutomatedBookingChannel } from '@elara/domain/models/schemas/itinerary-item-schemas';

import { bookingStatusBadgeProps, resolveBookingDisplayStatus } from '#lib/booking-status.js';
import { useCanRefreshPricing } from '#lib/can-refresh-pricing.js';
import { useFirstAvailableImage } from '#lib/use-first-available-image.js';
import { useRefreshPricing } from '#lib/useRefreshPricing.js';
import { useItineraryState, useNavigate, useRouterState, useTripsState } from '#states/hooks.js';
import { getFallbackCoverImageIcon } from '#ui/chat/utils.js';
import {
  AppImage,
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '#ui/index.js';
import { IconSizes, MaterialIcon } from '#ui/MaterialIcon.js';
import { PriceStatusIndicator } from '#ui/PriceStatusIndicator.js';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '#ui/tooltip.js';

import { isRoomUnavailable } from './booking-grouping.js';
import { ROW_GRID_CLASSES } from './constants.js';
import { SubtitleChips } from './SubtitleChips.js';

/**
 * Uniform table-style row for any itinerary item: a thumbnail, then the
 * title + subtitle (flights show their inline flight summary here), the
 * price, a primary action, and an overflow menu (refresh pricing + remove).
 * The thumbnail and title link through to the relevant guide.
 */
export function BookingRow({ item }: { item: ItineraryItem }): React.ReactNode {
  const itineraryState = useItineraryState();
  const tripsState = useTripsState();
  const routerState = useRouterState();
  const { navigate } = useNavigate();
  const tripId = tripsState.current?.id;

  // Rooms show the room-type name (the hotel is the section header). `roomLabel` resolves it live
  // from the RoomOption / referenceData, so an external stay reads its captured room name; other
  // item types keep their persisted display title.
  const rowTitle = item.type === 'room' ? (item.roomLabel ?? item.displayTitle) : item.displayTitle;

  const refreshModel = item.type === 'flight' ? item.flightOption : item.type === 'room' ? item.roomOption : undefined;
  const canRefresh = useCanRefreshPricing(item.type, refreshModel);
  const {
    canRefresh: showRefresh,
    isRefreshing,
    refresh,
  } = useRefreshPricing({
    type: item.type === 'flight' ? 'flight' : 'room',
    referenceId: item.referenceId,
    enabled: canRefresh,
    label: item.displayTitle,
  });

  const unavailable =
    item.type === 'room'
      ? isRoomUnavailable(item)
      : item.type === 'flight'
        ? !item.isCancelled &&
          // Prefer the server-denormalized verdict; fall back to the live
          // FlightOption doc when it hasn't been rechecked yet.
          (item.availabilityState === 'unavailable' || item.flightOption?.isAvailable === false)
        : false;
  const dimmed = (unavailable || item.isCancelled) && !item.isBooked;

  // Planned place/activity/custom items get a dedicated "Add booking details"
  // button (the user records an external booking themselves). An external room — no priced
  // RoomOption behind it — takes the same path: checkout has no rate to book, so its primary
  // action is recording the details, not "Book".
  const canAddBookingDetails =
    !item.isBooked &&
    !item.isApproved &&
    !item.isCancelled &&
    !unavailable &&
    item.type !== 'flight' &&
    (item.type !== 'room' || !item.referenceId);
  // Flights and places are user-managed: when they aren't showing the dedicated
  // button, the booking details stay editable from the overflow menu.
  const canEditBookingMenu = (item.type === 'flight' || item.type === 'place') && !canAddBookingDetails;
  // Rooms are fulfilled by the Elara team once booked/approved, so they stay protected; a pending
  // cancellation is in limbo (the reservation may still be live) and stays protected too. Flights
  // and places are user-managed and removable any time.
  const canRemove = item.type === 'room' ? !item.isBooked && !item.isApproved && !item.isCancellationPending : true;
  const hasMenuActions = canEditBookingMenu || showRefresh || canRemove;

  // Planned items carry no badge — "planned" is the implicit default here, so
  // a label would just be noise. Only exceptional states get a badge.
  const displayStatus = resolveBookingDisplayStatus({
    ...bookingStatusFlagsOf(item),
    isUnavailable: unavailable,
  });
  const badge = displayStatus === 'planned' ? undefined : bookingStatusBadgeProps(displayStatus);

  // An Elara-sourced item links to its own entity's guide (the row title is that entity). A room with
  // no priced RoomOption — an externally-booked stay the concierge imported — has no referenceId, so
  // it falls back to the stay's guide: the matched catalog hotel, else the attached Google Place.
  const guidePreview =
    item.referenceId && (item.type === 'flight' || item.type === 'place' || item.type === 'room')
      ? { preview: item.type, id: item.referenceId }
      : item.guideRef
        ? { preview: item.guideRef.kind, id: item.guideRef.id }
        : undefined;
  const guideHref = guidePreview ? routerState.buildUrlWithHashParams(guidePreview) : undefined;

  // Flight checkout runs off the searched flight, so one the user booked elsewhere has nothing to
  // book — its booking details are edited from the overflow menu instead.
  const canBookFlight = item.type === 'flight' && item.referenceId != null;

  function bookFlight(): void {
    if (!tripId || !item.referenceId) return;
    navigate(`/checkout/flight/${tripId}/${item.referenceId}`);
  }

  function bookRoom(): void {
    if (!tripId || !item.id) return;
    navigate(`/checkout/hotel/${tripId}?items=${item.id}`);
  }

  return (
    <div className={cn(ROW_GRID_CLASSES, dimmed && 'opacity-40')}>
      <div className="flex flex-col gap-2 md:contents">
        {item.type === 'flight' ? (
          <div className="col-[1/3] min-w-0 space-y-2">
            {flightLegs(item).map((leg, i) => (
              <div key={i} className="flex items-center gap-4">
                <FlightLegThumbnail leg={leg} href={guideHref} />
                <div className="min-w-0 flex-1">
                  <div className="font-medium break-words min-w-0" title={leg.title}>
                    {guideHref ? (
                      <a href={guideHref} className="hover:text-accent">
                        {leg.title}
                      </a>
                    ) : (
                      leg.title
                    )}
                  </div>
                  <SubtitleChips chips={leg.parts} />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <>
            <RowThumbnail item={item} href={guideHref} />
            <div className="min-w-0 flex-1">
              {/* For a room, show the room-type name (reactive, so an external stay reads its
                  referenceData.roomName rather than a persisted "Room"); the hotel is the section header. */}
              <div className="font-medium break-words min-w-0" title={rowTitle}>
                {guideHref ? (
                  <a href={guideHref} className="hover:text-accent">
                    {rowTitle}
                  </a>
                ) : (
                  rowTitle
                )}
              </div>

              <SubtitleChips chips={item.type === 'room' ? roomSubtitleChips(item) : placeSubtitleChips(item)} />
            </div>
          </>
        )}
      </div>

      <div className="flex flex-col gap-2 md:contents">
        <div className="flex items-center gap-3 md:contents">
          <div>
            {badge &&
              // Only operator-claimed manual bookings ('approved', non-GDS) get the "team will book"
              // tooltip; automated Sabre bookings show a plain status badge.
              (displayStatus === 'approved' && !isAutomatedBookingChannel(item.booking?.channel) ? (
                <TooltipProvider delayDuration={200}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="inline-flex">
                        <Badge variant={badge.variant} size="sm">
                          {badge.label}
                        </Badge>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>Our team will make this booking for you.</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ) : (
                <Badge variant={badge.variant} size="sm">
                  {badge.label}
                </Badge>
              ))}
          </div>

          <div className="order-first md:order-none md:text-right">
            {item.cost && (
              <div className="flex flex-col items-start gap-0.5 md:items-end">
                <span
                  className={cn(
                    'font-medium text-sm',
                    !item.cost.amount && 'text-muted-foreground',
                    (unavailable || item.isCancelled || item.isCancellationPending) && 'line-through',
                  )}
                >
                  {item.formattedCost}
                </span>
                <BookingPriceStatus item={item} />
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-1 md:justify-end">
          {/* A pending cancellation is neither bookable nor gone — keep "View booking" so the user can
              see the cancellation-in-progress details, and never offer "Book" (ELARA-872). */}
          {item.isBooked || item.isApproved || item.isCancellationPending ? (
            <Button
              variant="outline"
              className="flex-1 justify-center md:flex-none"
              onClick={() => itineraryState.openViewBookingDialog(item)}
            >
              <MaterialIcon icon="local_activity" size={IconSizes.sm} />
              <span>View booking</span>
            </Button>
          ) : item.isCancelled || unavailable ? null : canBookFlight ? (
            <Button variant="default" className="flex-1 justify-center md:flex-none" onClick={bookFlight}>
              <MaterialIcon icon="shopping_cart_checkout" size={IconSizes.sm} />
              <span>Book</span>
            </Button>
          ) : item.type === 'room' && item.referenceId ? (
            <Button variant="default" className="flex-1 justify-center md:flex-none" onClick={bookRoom}>
              <MaterialIcon icon="shopping_cart_checkout" size={IconSizes.sm} />
              <span>Book</span>
            </Button>
          ) : canAddBookingDetails ? (
            <Button
              variant="outline"
              className="flex-1 justify-center md:flex-none"
              onClick={() => itineraryState.openBookingDialog(item)}
            >
              <MaterialIcon icon="add" size={IconSizes.sm} />
              <span>Add booking</span>
            </Button>
          ) : null}

          {hasMenuActions && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="icon" size="icon" onClick={(e) => e.stopPropagation()}>
                  <MaterialIcon icon="more_vert" size={IconSizes.sm} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {canEditBookingMenu && (
                  <DropdownMenuItem onClick={() => setTimeout(() => itineraryState.openBookingDialog(item), 50)}>
                    <MaterialIcon icon={item.isBooked ? 'edit' : 'check'} className="mr-2" size={IconSizes.sm} />
                    {item.isBooked ? 'Edit booking' : 'Add booking'}
                  </DropdownMenuItem>
                )}
                {showRefresh && (
                  <DropdownMenuItem onClick={refresh} disabled={isRefreshing}>
                    <MaterialIcon
                      icon="refresh"
                      className={cn('mr-2', isRefreshing && 'animate-spin')}
                      size={IconSizes.sm}
                    />
                    {isRefreshing ? 'Refreshing…' : 'Refresh pricing'}
                  </DropdownMenuItem>
                )}
                {canRemove && (
                  <DropdownMenuItem
                    onClick={() => setTimeout(() => itineraryState.openDeleteDialog(item), 50)}
                    className="text-destructive"
                  >
                    <MaterialIcon icon="delete" className="mr-2" size={IconSizes.sm} />
                    Remove from itinerary
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Small fixed-size row thumbnail: the item's cover image (room/place) or an
 * icon fallback. Wrapped in a guide link when one is available.
 */
function RowThumbnail({ item, href }: { item: ItineraryItem; href: string | undefined }): React.ReactNode {
  // Every URL this item has, not just the first, and PROBED: this thumbnail is a CSS
  // `background-image`, which reports nothing when its URL 404s — a booked room whose stored photo has
  // rotted just left a blank box where a picture should be. `coverImageUrls` puts the stay's photos
  // behind the room's own, so such a room falls through to its hotel rather than to the icon.
  const { src: imageUrl } = useFirstAvailableImage(item.coverImageUrls, { probe: true });
  const fallbackIcon = item.type === 'flight' ? 'flight_takeoff' : getFallbackCoverImageIcon(item.type);

  return React.createElement(
    href ? 'a' : 'div',
    {
      ...(href ? { href } : {}),
      className: cn(
        // Mobile: full-bleed hero. The card has p-4 and the thumbnail is a
        // stretched flex child (plain negative margins won't widen it), so set
        // an explicit `100% + 2rem` width plus negative margins to reach the
        // card edges. md+: fixed inset thumbnail.
        'block aspect-[3/2] shrink-0 bg-cover bg-center',
        'w-[calc(100%+2rem)] -mx-4 -mt-4 rounded-t-lg mb-2',
        'md:aspect-auto md:h-14 md:w-20 md:mx-0 md:mt-0 md:mb-0 md:rounded-sm',
        !imageUrl && 'flex items-center justify-center bg-background-tertiary',
      ),
      style: imageUrl ? { backgroundImage: `url(${imageUrl})` } : undefined,
    },
    !imageUrl && (
      <MaterialIcon icon={fallbackIcon} size={IconSizes.md} className="text-muted-foreground/40" weight="light" />
    ),
  );
}

/**
 * Thumbnail for a flight leg: the airline logo on a neutral tile, falling back
 * to a plane icon when the carrier provides no logo.
 */
function FlightLegThumbnail({ leg, href }: { leg: FlightLeg; href: string | undefined }): React.ReactNode {
  return React.createElement(
    href ? 'a' : 'span',
    {
      ...(href ? { href } : {}),
      className: 'flex h-14 w-20 shrink-0 items-center justify-center rounded-sm bg-background-tertiary',
    },
    leg.airlineLogoUrl ? (
      <AppImage src={leg.airlineLogoUrl} alt="" className="max-h-8 max-w-[80%] object-contain" />
    ) : (
      <MaterialIcon icon={leg.icon} size={IconSizes.md} className="text-muted-foreground/40" weight="light" />
    ),
  );
}

function BookingPriceStatus({ item }: { item: ItineraryItem }): React.ReactNode {
  const tripsState = useTripsState();
  const tripId = tripsState.current?.id;
  if (!tripId) return null;

  const bookingStatus = item.booking?.status;
  if (
    bookingStatus === 'approved' ||
    bookingStatus === 'booked' ||
    bookingStatus === 'cancellation_pending' ||
    bookingStatus === 'cancelled'
  )
    return null;

  if (item.type === 'flight') {
    const flight = item.flightOption;
    if (!flight || !flight.isAvailable) return null;
    return <PriceStatusIndicator type="flight" model={flight} />;
  }

  if (item.type === 'room') {
    const room = item.roomOption;
    if (!room || !room.isAvailable) return null;
    const meta = item.metadata as RoomSelection | undefined;
    return (
      <PriceStatusIndicator type="room" model={room} productCode={meta?.productCode} occupancy={meta?.occupancy} />
    );
  }

  return null;
}
